import React from "react";

import { Control, DeepMap, FieldError, useForm } from "react-hook-form";

import { ReportFormInputs } from "../../screens/ReportScreen";

import {
  TemplateQuestionFragment,
  ReportMadFragment,
} from "../../graphql/fragments";
import { ReportAnswer, UserRole } from "../../graphql/types";

import {
  // Text Short
  TemplateQuestionTextShort,
  TemplateQuestionTextShortPreview,
  isTemplateQuestionTextShort,
  // Text Long
  TemplateQuestionTextLong,
  TemplateQuestionTextLongPreview,
  isTemplateQuestionTextLong,
  // Selector Single
  TemplateQuestionSelectorSingle,
  TemplateQuestionSelectorSinglePreview,
  isTemplateQuestionSelectorSingle,
  // Selector Multiple
  TemplateQuestionSelectorMultiple,
  TemplateQuestionSelectorMultiplePreview,
  isTemplateQuestionSelectorMultiple,
  // Skill
  TemplateQuestionSkill,
  TemplateQuestionSkillPreview,
  isTemplateQuestionSkill,
  // Evaluation
  TemplateQuestionEvaluation,
  TemplateQuestionEvaluationPreview,
  isTemplateQuestionEvaluation,
} from "./TemplateQuestionsImpl";
import useTranslations from "../../hooks/useTranslations";

export interface TemplateQuestionImplProps {
  question: TemplateQuestionFragment;
  answers: ReportAnswer[];
  role: string;
  mad: ReportMadFragment;
  control: Control<ReportFormInputs>;
  register: ReturnType<typeof useForm>["register"];
  setValue: ReturnType<typeof useForm>["setValue"];
  errors: DeepMap<ReportFormInputs, FieldError>;
  status: TemplateQuestionFormStatus;
  name: string;
  displayIndex: string;
  needsToBeFilled: boolean;
  onReview: boolean;
  autoFocus: boolean;
}

export interface TemplateQuestionPreviewImplProps {
  question: TemplateQuestionFragment;
  displayIndex: string;
}

export type TemplateQuestionConfig<T extends TemplateQuestionFragment> = {
  Component: React.ElementType<TemplateQuestionImplProps>;
  PreviewComponent: React.ElementType<TemplateQuestionPreviewImplProps>;
  typeValidator: (q: TemplateQuestionFragment) => q is T;
};

const templateQuestionConfigs: TemplateQuestionConfig<TemplateQuestionFragment>[] = [
  {
    Component: TemplateQuestionSelectorSingle,
    PreviewComponent: TemplateQuestionSelectorSinglePreview,
    typeValidator: isTemplateQuestionSelectorSingle,
  },
  {
    Component: TemplateQuestionSelectorMultiple,
    PreviewComponent: TemplateQuestionSelectorMultiplePreview,
    typeValidator: isTemplateQuestionSelectorMultiple,
  },
  {
    Component: TemplateQuestionTextShort,
    PreviewComponent: TemplateQuestionTextShortPreview,
    typeValidator: isTemplateQuestionTextShort,
  },
  {
    Component: TemplateQuestionTextLong,
    PreviewComponent: TemplateQuestionTextLongPreview,
    typeValidator: isTemplateQuestionTextLong,
  },
  {
    Component: TemplateQuestionSkill,
    PreviewComponent: TemplateQuestionSkillPreview,
    typeValidator: isTemplateQuestionSkill,
  },
  {
    Component: TemplateQuestionEvaluation,
    PreviewComponent: TemplateQuestionEvaluationPreview,
    typeValidator: isTemplateQuestionEvaluation,
  },
];

export type ReportAnswersByRole<T extends ReportAnswer> = {
  [key in Exclude<UserRole, "administrator">]: T[];
};

export function getReportAnswersByRole<T extends ReportAnswer>(
  answers: ReportAnswer[],
  parseStrategy: (r: ReportAnswer[]) => T[],
  mad: ReportMadFragment
): ReportAnswersByRole<T> {
  return {
    talent: parseStrategy(answers.filter((a) => a.user.id === mad.talent.id)),
    referent: parseStrategy(
      // TODO: Uncomment for correct parsing of referents
      answers.filter((a) => mad.referents.map(r => r.id).includes(a.user.id))
    ),
  };
}

export type TemplateQuestionFormStatus =
  | "to_be_filled_talent"
  | "to_be_filled_evaluator"
  | "talent_review"
  | "evaluator_review"
  | "hr_review";

type TemplateQuestionProps = {
  question: TemplateQuestionFragment;
  answers: ReportAnswer[];
  mad: ReportMadFragment;
  control: Control<ReportFormInputs>;
  register: ReturnType<typeof useForm>["register"];
  setValue: ReturnType<typeof useForm>["setValue"];
  errors: DeepMap<ReportFormInputs, FieldError>;
  displayIndex: string;
  name: string;
  status: TemplateQuestionFormStatus;
  role: string;
  autoFocus: boolean;
};

type TemplateQuestionPreviewProps = {
  question: TemplateQuestionFragment;
  index: number;
  displayIndex: string;
  preview: true;
  status: TemplateQuestionFormStatus;
};

function TemplateQuestion(props: TemplateQuestionProps);
function TemplateQuestion(props: TemplateQuestionPreviewProps);
function TemplateQuestion({
  question,
  status,
  ...props
}: TemplateQuestionProps | TemplateQuestionPreviewProps) {
  const t = useTranslations()
  const Config = templateQuestionConfigs.find((c) => c.typeValidator(question));

  if ("preview" in props) {
    return (
      Config ? (
        <div id={`questionPreview-${question.id}`}>
          <Config.PreviewComponent question={question} {...props} />
        </div>
      ) : (
        <div>{t("appErrors.componentUnknown", {key: question.type})}</div>
      )
    );
  }

  const needsToBeFilled =
    status === "to_be_filled_evaluator" || status === "to_be_filled_talent";
  const onReview =
    status === "talent_review" ||
    status === "evaluator_review" ||
    status === "hr_review";

  return (
    Config ? (
      <div id={`question-${question.id}`}>
        <Config.Component
          question={question}
          needsToBeFilled={needsToBeFilled}
          onReview={onReview}
          status={status}
          {...props}
        />
      </div>
    ) : (
      <div>{t("appErrors.componentUnknown", {key: question.type})}</div>
    )
  );
}

export default React.memo(TemplateQuestion);
